import type { DeliveryLeaderRow } from '../../api/types.js';
import { color, fontSize, initialsOf, personAccent, radius, tint } from '../../theme/tokens.js';

/**
 * Leader pills (FR-D07, PRD-Delivery §6.2, mirroring `components/Pills/RegionPills.tsx`).
 *
 * **A navigation set, not a query result.** Every leader renders in every state, so the way into a
 * division never disappears because that division happened to be filtered out — the same rule the
 * region pills follow, and the reason the API returns the full leader list on all four endpoints.
 *
 * **Each pill toggles its own leader in or out of the selection** (owner request 2026-10-20,
 * FR-D07a): a click adds that leader, a second click on the same pill removes them, and removing
 * the last one is the all-managers view. Several selected is the union of their divisions — the
 * page asks the server for it as one scope rather than adding leaders up, because two managers
 * report to two leaders and a sum would count their teams twice (FR-D05).
 *
 * **Avatar and name only, no visible count.** PRD-Delivery §6.2 specifies exactly that, and the
 * region pills' trailing count is not carried over: leader counts *do not sum to the company
 * total* (two managers report to two leaders — FR-D05), and a number sitting in a row of pills
 * invites exactly the addition that would be wrong.
 *
 * The counts stay in the **accessible name**, where they are context for one pill rather than a
 * column to add up. That is additive to the blueprint, which is silent on assistive technology.
 */

export interface LeaderPillsProps {
  leaders: DeliveryLeaderRow[];
  /** The selected leader keys — empty for the all-managers state. */
  selected: readonly string[];
  /** Called with the whole next selection: the clicked key added, or removed if it was in. */
  onChange: (leaderKeys: string[]) => void;
  loading?: boolean;
}

export function LeaderPills({ leaders, selected, onChange, loading }: LeaderPillsProps) {
  if (loading) {
    return (
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {Array.from({ length: 5 }, (_unused, i) => (
          <div key={i} className="skeleton" style={{ height: 30, width: 140 }} aria-hidden="true" />
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        marginBottom: 18,
      }}
      aria-label="Leaders"
    >
      <span
        style={{
          fontSize: fontSize.caption,
          fontWeight: 600,
          letterSpacing: '.5px',
          color: color.inkSubtle,
          textTransform: 'uppercase',
          marginRight: 2,
        }}
      >
        Leaders
      </span>

      {leaders.map((leader) => {
        const accent = personAccent(leader.leaderKey);
        const isSelected = selected.includes(leader.leaderKey);

        return (
          <button
            key={leader.leaderKey}
            type="button"
            className="chip"
            // A toggle per pill (FR-D07a): in if it was out, out if it was in. Deselecting the
            // last one is the all-managers view, so there is no separate "All" control to find.
            onClick={() =>
              onChange(
                isSelected
                  ? selected.filter((key) => key !== leader.leaderKey)
                  : [...selected, leader.leaderKey],
              )
            }
            aria-pressed={isSelected}
            // The visible pill is a name and an avatar; the announced name spells out what the
            // counts are, including that they are per division rather than a share of a total.
            aria-label={`${leader.name}, ${leader.managerCount} ${
              leader.managerCount === 1 ? 'manager' : 'managers'
            }, ${leader.recruiterCount} recruiters${isSelected ? ' — selected' : ''}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: isSelected ? tint(accent, 8) : color.surface,
              border: `1px solid ${isSelected ? accent : color.borderStrong}`,
              borderRadius: radius.pill,
              padding: '5px 14px 5px 6px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 24,
                height: 24,
                flex: 'none',
                borderRadius: radius.circle,
                background: accent,
                color: color.onAccent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 700,
              }}
            >
              {initialsOf(leader.name)}
            </span>
            <span
              aria-hidden="true"
              style={{
                fontSize: fontSize.bodyStrong,
                fontWeight: 600,
                color: isSelected ? accent : color.inkRow,
              }}
            >
              {leader.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}
